import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import {
  useDeleteSoftwareSeat,
  useSaveSoftwareSeat,
  type SoftwareSeat,
} from '@/features/software/hooks';
import { friendlySoftwareError, type SeatProfile } from '@/features/software/model';
import { todayIsoDate } from '@/lib/formatters';

const SOMEONE_ELSE = '__other';

export function SoftwareSeatDialog({
  open,
  seat,
  subscriptionId,
  subscriptionName,
  profiles,
  takenProfileIds,
  canEdit,
  onOpenChange,
}: {
  open: boolean;
  seat: SoftwareSeat | null;
  subscriptionId: string;
  subscriptionName: string;
  profiles: SeatProfile[];
  takenProfileIds: Set<string>;
  canEdit: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const save = useSaveSoftwareSeat();
  const remove = useDeleteSoftwareSeat();
  const [holder, setHolder] = useState('');
  const [assigneeName, setAssigneeName] = useState('');
  const [login, setLogin] = useState('');
  const [licenseType, setLicenseType] = useState('');
  const [assignedOn, setAssignedOn] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setHolder(seat ? (seat.profile_id ?? SOMEONE_ELSE) : '');
    setAssigneeName(seat && !seat.profile_id ? (seat.assignee_name ?? '') : '');
    setLogin(seat?.login ?? '');
    setLicenseType(seat?.license_type ?? '');
    setAssignedOn(seat?.assigned_on ?? (seat ? '' : todayIsoDate()));
    setNotes(seat?.notes ?? '');
    setConfirmDelete(false);
  }, [open, seat]);

  const available = profiles.filter((p) => !takenProfileIds.has(p.id) || p.id === seat?.profile_id);
  const isOther = holder === SOMEONE_ELSE;
  const ready = isOther ? assigneeName.trim().length > 0 : holder.length > 0;
  const pending = save.isPending || remove.isPending;

  const pickHolder = (value: string) => {
    setHolder(value);
    if (!login.trim() && value !== SOMEONE_ELSE) {
      const person = profiles.find((p) => p.id === value);
      if (person) setLogin(person.email);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!canEdit || !ready) return;
            void save
              .mutateAsync({
                id: seat?.id,
                subscriptionId,
                profileId: isOther ? null : holder,
                assigneeName,
                login,
                licenseType,
                assignedOn,
                notes,
              })
              .then(() => {
                toast.success(seat ? 'Seat updated.' : 'Seat added.');
                onOpenChange(false);
              })
              .catch((err) => toast.error(friendlySoftwareError(err, 'Could not save the seat.')));
          }}
        >
          <DialogHeader>
            <DialogTitle>{seat ? 'Edit seat' : 'Add a seat'}</DialogTitle>
            <DialogDescription>
              Who uses {subscriptionName}, and how they sign in.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Person</Label>
              <Select value={holder} onValueChange={pickHolder} disabled={!canEdit}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a person" />
                </SelectTrigger>
                <SelectContent>
                  {available.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.full_name || p.email}
                    </SelectItem>
                  ))}
                  <SelectItem value={SOMEONE_ELSE}>Someone not in Atlas…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isOther && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="seat-name">Name</Label>
                <Input
                  id="seat-name"
                  value={assigneeName}
                  onChange={(e) => setAssigneeName(e.target.value)}
                  placeholder="e.g. Freelance designer, or a shared inbox"
                  disabled={!canEdit}
                  required
                />
              </div>
            )}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="seat-login">Login</Label>
              <Input
                id="seat-login"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                placeholder="Email or username they sign in with"
                autoComplete="off"
                disabled={!canEdit}
              />
              <p className="text-xs text-muted-foreground">
                Logins only. Passwords go on the account’s page under Software → Accounts.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="seat-license">License or role</Label>
              <Input
                id="seat-license"
                value={licenseType}
                onChange={(e) => setLicenseType(e.target.value)}
                placeholder="e.g. Admin, Premium"
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="seat-since">Since</Label>
              <Input
                id="seat-since"
                type="date"
                value={assignedOn}
                onChange={(e) => setAssignedOn(e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="seat-notes">Notes</Label>
              <Textarea
                id="seat-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                disabled={!canEdit}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {canEdit && seat ? (
              confirmDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={pending}
                  onClick={() =>
                    void remove
                      .mutateAsync(seat.id)
                      .then(() => {
                        toast.success('Seat removed.');
                        onOpenChange(false);
                      })
                      .catch((err) =>
                        toast.error(friendlySoftwareError(err, 'Could not remove the seat.')),
                      )
                  }
                >
                  Confirm remove
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setConfirmDelete(true)}>
                  Remove seat
                </Button>
              )
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {canEdit && (
                <Button type="submit" disabled={pending || !ready}>
                  {seat ? 'Save' : 'Add seat'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
